/**
 * The shelf models, built procedurally so nothing is fetched: an anatomical torso with
 * organs, a small-molecule drug (ball and stick, grouped by element), the study's site map
 * with risk-coloured pins, and the dosing schedule as a timeline of visit blocks. Every
 * model is a list of named parts with a rest position and an explode direction, so the
 * layer can stand it on the table, pin parts and explode it. Geometry only; no scene objects,
 * so it runs under Vitest.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { ModelKind, Site, VisitDefinition } from '@clinspace/shared';

export interface PartSpec {
  name: string;
  geometry: THREE.BufferGeometry;
  colour: string;
  /** Rest position of the part's origin in model space (metres, model base at y = 0). */
  position: [number, number, number];
  /** Where the part goes when fully exploded, relative to its rest position. */
  explode: [number, number, number];
  opacity?: number;
  /** Shown on hover beneath the part name. */
  detail?: string;
}

export interface ModelSpec {
  kind: ModelKind;
  title: string;
  parts: PartSpec[];
  /** Height of the assembled model in metres. */
  height: number;
  /** Radius around the base for placement on the table. */
  radius: number;
}

export interface ModelStudyData {
  sites: Site[];
  /** Site id to RAG colour name from the risk dashboard. */
  siteRag: Map<string, 'Red' | 'Amber' | 'Green'>;
  visitSchedule: VisitDefinition[];
  /** Per visit code: doses given and doses missed across the study. */
  dosing: Map<string, { given: number; missed: number }>;
  drug: string;
}

export const MODEL_TITLES: Record<ModelKind, string> = {
  torso: 'Anatomical torso',
  molecule: 'Study drug molecule',
  siteMap: 'Site map',
  dosing: 'Dosing schedule',
};

/** What each model is for in a review, shown on the shelf sign. */
export const MODEL_USES: Record<ModelKind, string> = {
  torso:
    "Point at the organ a safety signal concerns (the liver for a Hy's law case, the heart for QT) and pin it so the room talks about the same thing.",
  molecule:
    'The study drug, for discussing the mechanism behind an adverse event or an interaction with a concomitant medication.',
  siteMap:
    "This study's sites as pins coloured by risk score, for a monitoring discussion about where to send a CRA.",
  dosing:
    "This study's visit schedule: block height is doses given, amber where doses were missed, for dosing compliance.",
};

export const MODEL_HINTS: Record<ModelKind, string> = {
  torso: 'Organs explode outward; pin the one an adverse event concerns',
  molecule: 'Ball and stick, grouped by element',
  siteMap: 'Sites as pins coloured by risk score',
  dosing: 'Visit blocks sized by doses given, amber where doses were missed',
};

const RAG_COLOUR = { Red: '#ff5d73', Amber: '#f2b44b', Green: '#3fd69a' };

/** Rough latitude bands for the countries the studies use (0 top .. 1 bottom of the map). */
const COUNTRY_Y: Record<string, number> = {
  CA: 0.22,
  US: 0.36,
  GB: 0.26,
  UK: 0.26,
  DE: 0.28,
  FR: 0.31,
  ES: 0.36,
  AU: 0.8,
};

export function buildModel(kind: ModelKind, data: ModelStudyData): ModelSpec {
  switch (kind) {
    case 'torso':
      return torso();
    case 'molecule':
      return molecule(data.drug);
    case 'siteMap':
      return siteMap(data);
    case 'dosing':
      return dosing(data);
  }
}

function torso(): ModelSpec {
  const parts: PartSpec[] = [];
  const shell = new THREE.CapsuleGeometry(0.16, 0.26, 6, 18);
  shell.scale(1, 1, 0.65);
  parts.push({
    name: 'Torso',
    geometry: shell,
    colour: '#d9c3b0',
    position: [0, 0.3, 0],
    explode: [0, 0, 0],
    opacity: 0.22,
    detail: 'Translucent body shell',
  });
  const organ = (
    name: string,
    geometry: THREE.BufferGeometry,
    colour: string,
    x: number,
    y: number,
    z: number,
    detail: string,
  ) =>
    parts.push({
      name,
      geometry,
      colour,
      position: [x, y, z],
      explode: [x * 2.2, (y - 0.3) * 1.2, z * 2.2 + (z === 0 ? 0.08 : 0)],
      detail,
    });
  const lung = () => {
    const g = new THREE.SphereGeometry(0.055, 14, 10);
    g.scale(0.8, 1.5, 0.7);
    return g;
  };
  organ('Left lung', lung(), '#e8a0a8', 0.055, 0.42, 0, 'Respiratory: pneumonitis, dyspnoea');
  organ('Right lung', lung(), '#e8a0a8', -0.055, 0.42, 0, 'Respiratory: pneumonitis, dyspnoea');
  organ(
    'Heart',
    new THREE.SphereGeometry(0.038, 14, 10),
    '#b0233a',
    0.015,
    0.4,
    0.035,
    'Cardiac: QT, myocarditis, ECG',
  );
  const liver = new THREE.SphereGeometry(0.06, 14, 10);
  liver.scale(1.3, 0.7, 0.8);
  organ('Liver', liver, '#8b3a2a', -0.05, 0.3, 0.03, "Hepatic: ALT, AST, bilirubin, Hy's law");
  organ(
    'Stomach',
    new THREE.SphereGeometry(0.04, 14, 10),
    '#d9a066',
    0.045,
    0.3,
    0.03,
    'GI: nausea, vomiting',
  );
  const kidney = () => {
    const g = new THREE.SphereGeometry(0.025, 12, 8);
    g.scale(0.8, 1.4, 0.7);
    return g;
  };
  organ('Left kidney', kidney(), '#7a3b4a', 0.06, 0.22, -0.04, 'Renal: creatinine, eGFR');
  organ('Right kidney', kidney(), '#7a3b4a', -0.06, 0.22, -0.04, 'Renal: creatinine, eGFR');
  organ(
    'Intestines',
    new THREE.TorusGeometry(0.055, 0.02, 10, 20),
    '#e3b58f',
    0,
    0.17,
    0.03,
    'GI: diarrhoea, colitis',
  );
  return { kind: 'torso', title: MODEL_TITLES.torso, parts, height: 0.6, radius: 0.22 };
}

interface Atom {
  el: 'C' | 'N' | 'O' | 'F' | 'S';
  p: [number, number, number];
}

const ELEMENT = {
  C: { colour: '#5f6670', name: 'Carbon', r: 0.016 },
  N: { colour: '#3b6fd9', name: 'Nitrogen', r: 0.017 },
  O: { colour: '#ff5d73', name: 'Oxygen', r: 0.017 },
  F: { colour: '#3fd69a', name: 'Fluorine', r: 0.015 },
  S: { colour: '#e0c23a', name: 'Sulfur', r: 0.02 },
};

/** A hand-laid kinase-inhibitor-like scaffold: two aromatic rings, an amide and a tail. */
function moleculeGraph(): { atoms: Atom[]; bonds: [number, number][] } {
  const atoms: Atom[] = [];
  const bonds: [number, number][] = [];
  const s = 0.055;
  const ring = (cx: number, cy: number, hetero: number[] = []): number[] => {
    const ids: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      ids.push(atoms.length);
      atoms.push({
        el: hetero.includes(i) ? 'N' : 'C',
        p: [cx + Math.cos(a) * s, cy + Math.sin(a) * s, 0],
      });
    }
    for (let i = 0; i < 6; i++) bonds.push([ids[i]!, ids[(i + 1) % 6]!]);
    return ids;
  };
  const a = ring(-0.14, 0, [1]);
  const b = ring(0.14, 0.02, [0, 3]);
  // Amide bridge between the rings.
  const c1 = atoms.push({ el: 'C', p: [-0.055, 0.03, 0.02] }) - 1;
  const o1 = atoms.push({ el: 'O', p: [-0.05, 0.085, 0.045] }) - 1;
  const n1 = atoms.push({ el: 'N', p: [0.0, 0.0, 0.03] }) - 1;
  bonds.push([a[0]!, c1], [c1, o1], [c1, n1], [n1, b[3]!]);
  // Fluorine and a methyl on ring A, a sulfonyl tail on ring B.
  const f1 = atoms.push({ el: 'F', p: [-0.24, -0.06, 0.0] }) - 1;
  bonds.push([a[3]!, f1]);
  const c2 = atoms.push({ el: 'C', p: [-0.16, 0.095, -0.03] }) - 1;
  bonds.push([a[2]!, c2]);
  const s1 = atoms.push({ el: 'S', p: [0.24, 0.07, -0.02] }) - 1;
  const o2 = atoms.push({ el: 'O', p: [0.27, 0.12, 0.02] }) - 1;
  const o3 = atoms.push({ el: 'O', p: [0.27, 0.03, -0.06] }) - 1;
  const n2 = atoms.push({ el: 'N', p: [0.3, 0.1, -0.07] }) - 1;
  bonds.push([b[1]!, s1], [s1, o2], [s1, o3], [s1, n2]);
  return { atoms, bonds };
}

function molecule(drug: string): ModelSpec {
  const { atoms, bonds } = moleculeGraph();
  const byElement = new Map<Atom['el'], THREE.BufferGeometry[]>();
  const bondGeometries: THREE.BufferGeometry[] = [];
  const centre = new THREE.Vector3();
  for (const atom of atoms) centre.add(new THREE.Vector3(...atom.p));
  centre.divideScalar(atoms.length);
  for (const atom of atoms) {
    const g = new THREE.SphereGeometry(ELEMENT[atom.el].r, 12, 8);
    g.translate(atom.p[0] - centre.x, atom.p[1] - centre.y, atom.p[2] - centre.z);
    if (!byElement.has(atom.el)) byElement.set(atom.el, []);
    byElement.get(atom.el)!.push(g);
  }
  const up = new THREE.Vector3(0, 1, 0);
  for (const [i, j] of bonds) {
    const a = new THREE.Vector3(...atoms[i]!.p).sub(centre);
    const b = new THREE.Vector3(...atoms[j]!.p).sub(centre);
    const len = a.distanceTo(b);
    const g = new THREE.CylinderGeometry(0.004, 0.004, len, 6);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const q = new THREE.Quaternion().setFromUnitVectors(up, b.clone().sub(a).normalize());
    g.applyQuaternion(q);
    g.translate(mid.x, mid.y, mid.z);
    bondGeometries.push(g);
  }
  const parts: PartSpec[] = [];
  const y = 0.22;
  parts.push({
    name: 'Bonds',
    geometry: mergeGeometries(bondGeometries, false)!,
    colour: '#aab2bc',
    position: [0, y, 0],
    explode: [0, 0, 0],
    detail: `${bonds.length} bonds`,
  });
  const spread: Record<Atom['el'], [number, number, number]> = {
    C: [0, 0.16, 0],
    N: [0.16, 0.04, 0.06],
    O: [-0.16, 0.04, 0.06],
    F: [-0.1, -0.08, -0.1],
    S: [0.14, -0.08, -0.1],
  };
  for (const [el, list] of byElement) {
    parts.push({
      name: ELEMENT[el].name,
      geometry: mergeGeometries(list, false)!,
      colour: ELEMENT[el].colour,
      position: [0, y, 0],
      explode: spread[el],
      detail: `${list.length} atom${list.length === 1 ? '' : 's'} of ${drug}`,
    });
  }
  return { kind: 'molecule', title: MODEL_TITLES.molecule, parts, height: 0.44, radius: 0.3 };
}

function siteMap(data: ModelStudyData): ModelSpec {
  const W = 0.64;
  const D = 0.42;
  const parts: PartSpec[] = [
    {
      name: 'Map',
      geometry: new THREE.BoxGeometry(W, 0.02, D),
      colour: '#253242',
      position: [0, 0.01, 0],
      explode: [0, 0, 0],
      detail: `${data.sites.length} sites`,
    },
  ];
  // Stylised landmasses: a few rounded plates in a lighter tone.
  const land = (x: number, z: number, w: number, d: number, name: string) => {
    const g = new THREE.CylinderGeometry(1, 1, 0.006, 12);
    g.scale(w / 2, 1, d / 2);
    parts.push({
      name,
      geometry: g,
      colour: '#3d7bff',
      position: [x, 0.023, z],
      explode: [0, 0, 0],
      detail: 'Landmass',
    });
  };
  land(-0.18, -0.08, 0.26, 0.2, 'North America');
  land(0.06, -0.1, 0.16, 0.14, 'Europe');
  land(0.22, 0.14, 0.12, 0.09, 'Australia');
  for (const site of data.sites) {
    const x = (site.mapX - 0.5) * (W - 0.08);
    const z = ((COUNTRY_Y[site.countryCode] ?? 0.5) - 0.5) * (D - 0.06);
    const pin = mergeGeometries(
      [
        new THREE.CylinderGeometry(0.004, 0.004, 0.07, 6).translate(0, 0.035, 0),
        new THREE.SphereGeometry(0.014, 12, 8).translate(0, 0.075, 0),
      ],
      false,
    )!;
    const rag = data.siteRag.get(site.id) ?? 'Green';
    parts.push({
      name: `${site.id} ${site.city}`,
      geometry: pin,
      colour: RAG_COLOUR[rag],
      position: [x, 0.02, z],
      explode: [x * 0.6, 0.14, z * 0.6],
      detail: `${site.name} · ${site.country} · risk ${rag.toLowerCase()}`,
    });
  }
  return { kind: 'siteMap', title: MODEL_TITLES.siteMap, parts, height: 0.12, radius: 0.36 };
}

function dosing(data: ModelStudyData): ModelSpec {
  const schedule = [...data.visitSchedule].sort((a, b) => a.targetDay - b.targetDay);
  const L = 0.8;
  const min = schedule[0]?.targetDay ?? 0;
  const max = schedule[schedule.length - 1]?.targetDay ?? 1;
  const span = Math.max(1, max - min);
  const parts: PartSpec[] = [
    {
      name: 'Study timeline',
      geometry: new THREE.BoxGeometry(L + 0.06, 0.02, 0.1),
      colour: '#253242',
      position: [0, 0.01, 0],
      explode: [0, 0, 0],
      detail: `Day ${min} to day ${max}`,
    },
  ];
  const maxGiven = Math.max(1, ...[...data.dosing.values()].map((d) => d.given));
  schedule.forEach((visit, i) => {
    const x = ((visit.targetDay - min) / span) * L - L / 2;
    const d = data.dosing.get(visit.code) ?? { given: 0, missed: 0 };
    const h = 0.03 + (d.given / maxGiven) * 0.22;
    parts.push({
      name: visit.name,
      geometry: new THREE.BoxGeometry(0.03, h, 0.06).translate(0, h / 2, 0),
      // A dosing record covers a cycle of daily doses; amber when, across the study, the
      // visit averages more than one missed dose per two records.
      colour: d.missed > d.given * 0.5 ? '#f2b44b' : d.given > 0 ? '#3fd6ff' : '#7f8a98',
      position: [x, 0.02, 0],
      explode: [0, 0.1 + (i % 2) * 0.1, 0],
      detail: `Day ${visit.targetDay} · ${d.given} dose${d.given === 1 ? '' : 's'} given${d.missed ? `, ${d.missed} missed` : ''}`,
    });
  });
  return { kind: 'dosing', title: MODEL_TITLES.dosing, parts, height: 0.3, radius: 0.44 };
}

/** The part's position for an explode amount 0..1. */
export function partPosition(
  part: PartSpec,
  exploded: number,
  target = new THREE.Vector3(),
): THREE.Vector3 {
  return target.set(
    part.position[0] + part.explode[0] * exploded,
    part.position[1] + part.explode[1] * exploded,
    part.position[2] + part.explode[2] * exploded,
  );
}

export function triangleCount(spec: ModelSpec): number {
  return spec.parts.reduce((n, p) => {
    const index = p.geometry.index;
    return n + (index ? index.count : p.geometry.getAttribute('position').count) / 3;
  }, 0);
}

/** One merged, vertex-coloured geometry of the assembled model (for the shelf miniatures). */
export function mergedGeometry(spec: ModelSpec): THREE.BufferGeometry {
  const colour = new THREE.Color();
  // Translucent shells are left out so the organs show on the shelf miniature.
  const list = spec.parts
    .filter((p) => p.opacity === undefined)
    .map((p) => {
      const g = (
        p.geometry.index ? p.geometry.toNonIndexed() : p.geometry.clone()
      ) as THREE.BufferGeometry;
      for (const name of Object.keys(g.attributes))
        if (name !== 'position' && name !== 'normal') g.deleteAttribute(name);
      g.translate(p.position[0], p.position[1], p.position[2]);
      colour.set(p.colour);
      const n = g.getAttribute('position').count;
      const colours = new Float32Array(n * 3);
      for (let i = 0; i < n; i++) {
        colours[i * 3] = colour.r;
        colours[i * 3 + 1] = colour.g;
        colours[i * 3 + 2] = colour.b;
      }
      g.setAttribute('color', new THREE.BufferAttribute(colours, 3));
      return g;
    });
  return mergeGeometries(list, false)!;
}
